import { getUserAccountOrder, getNotBindOrder } from 'src/@api/login'
import { messageInfo } from 'src/components/Message'
import { filterValidUnboundOrders } from 'src/utils/chooseDeviceRedirect'
import { matchOrderForDevice } from 'src/utils/matchOrderForDevice'
import { switchDevice } from 'src/utils/switchDevice'

const BIND_ORDER_STORAGE_KEY = 'ontBindOrder'
const FROM_APP_STORAGE_KEY = 'spyx_from_app'
const MISSING_ORDER_MSG = 'Current order is missing'

const readSearchParams = (search = '') => {
  try {
    const raw = String(search || '')
    const query = raw.startsWith('?') ? raw.slice(1) : raw
    if (!query) return new URLSearchParams()

    return new URLSearchParams(query)
  } catch (e) {
    return new URLSearchParams()
  }
}

const readOrderIdFromSearch = (search = '') => {
  try {
    const params = readSearchParams(search)
    const orderId = params.get('orderId') || params.get('order_id')

    return orderId ? String(orderId) : null
  } catch (e) {
    return null
  }
}

/** URL 带 orderId 时写入 ontBindOrder；App 会话标记 from_app */
export const persistBindOrderIdFromSearch = (search = typeof window !== 'undefined' ? window.location.search : '') => {
  if (typeof window === 'undefined') return null

  const params = readSearchParams(search)
  if (params.get('from_app') === '1') {
    localStorage.setItem(FROM_APP_STORAGE_KEY, '1')
  }

  const orderId = readOrderIdFromSearch(search)
  if (!orderId) return null

  try {
    const prev = JSON.parse(localStorage.getItem(BIND_ORDER_STORAGE_KEY) || 'null')
    if (prev && typeof prev === 'object' && String(prev.id) === String(orderId)) {
      return orderId
    }
  } catch (e) {
    // ignore invalid cache
  }

  localStorage.setItem(BIND_ORDER_STORAGE_KEY, JSON.stringify({ id: orderId }))

  return orderId
}

export const readCachedBindOrderId = () => {
  if (typeof window === 'undefined') return null

  try {
    const ontBindOrder = JSON.parse(localStorage.getItem(BIND_ORDER_STORAGE_KEY) || 'null')

    return ontBindOrder?.id ? String(ontBindOrder.id) : null
  } catch (e) {
    return null
  }
}

/** 侧栏已选空 Package（有绑定单且无当前设备） */
export const isUnboundPackageSelectionActive = () => {
  if (typeof window === 'undefined') return false

  const orderId = readCachedBindOrderId()
  if (!orderId) return false

  try {
    const deviceUser = JSON.parse(localStorage.getItem('DeviceUser') || '{}') || {}
    const hasDevice =
      (deviceUser.device_id != null && String(deviceUser.device_id) !== '') ||
      (deviceUser.username != null && String(deviceUser.username) !== '')

    return !hasDevice
  } catch {
    return true
  }
}

/** 清除绑定单缓存与 URL 上的 orderId（已绑完 / 失效时用） */
export const clearBindOrderContext = () => {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(BIND_ORDER_STORAGE_KEY)
  } catch (e) {
    // ignore
  }

  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('orderId') && !url.searchParams.has('order_id')) return

    url.searchParams.delete('orderId')
    url.searchParams.delete('order_id')
    window.history.replaceState(window.history.state, '', url.toString())
  } catch (e) {
    // ignore
  }
}

const getBindOrderId = order => {
  const id = order?.id ?? order?.order_id

  return id != null && id !== '' ? String(id) : null
}

/** 用户选中套餐后写入 ontBindOrder */
export const cacheBindOrder = order => {
  if (typeof window === 'undefined') return null

  const id = getBindOrderId(order)
  if (!id) return null

  localStorage.setItem(BIND_ORDER_STORAGE_KEY, JSON.stringify({ ...order, id }))

  return id
}

/**
 * 侧栏选中空 Package：写入绑定单、清空当前设备选中并广播切换。
 * 使 Redux / localStorage 与 UI 高亮一致，不再保留上一个已绑定设备。
 */
export const selectUnboundPackage = ({ dispatch, order }) => {
  const orderId = cacheBindOrder(order)
  if (!orderId) return null

  syncOrderIdToUrl(orderId)

  switchDevice({
    dispatch,
    deviceUser: {},
    currentDevice: {}
  })

  return orderId
}

/** 无刷新把 orderId 同步到地址栏，避免刷新丢上下文 */
export const syncOrderIdToUrl = orderId => {
  if (typeof window === 'undefined' || !orderId) return

  try {
    const url = new URL(window.location.href)
    if (url.searchParams.get('orderId') === String(orderId)) return

    url.searchParams.set('orderId', String(orderId))
    url.searchParams.delete('order_id')
    window.history.replaceState(window.history.state, '', url.toString())
  } catch (e) {
    // ignore
  }
}

export const isFromAppSession = (search = typeof window !== 'undefined' ? window.location.search : '') => {
  const params = readSearchParams(search)
  if (params.get('from_app') === '1') return true
  if (typeof window === 'undefined') return false

  return localStorage.getItem(FROM_APP_STORAGE_KEY) === '1'
}

/** 跳转 setup 子页时带上 orderId / from_app */
export const appendSetupQuery = (path, orderId) => {
  if (!path) return path

  const resolvedOrderId =
    orderId ||
    readOrderIdFromSearch(typeof window !== 'undefined' ? window.location.search : '') ||
    readCachedBindOrderId()

  const fromApp = isFromAppSession(typeof window !== 'undefined' ? window.location.search : '')

  if (!resolvedOrderId && !fromApp) return path

  try {
    const [pathnameAndQuery, hash = ''] = String(path).split('#')
    const qIndex = pathnameAndQuery.indexOf('?')
    const pathname = qIndex >= 0 ? pathnameAndQuery.slice(0, qIndex) : pathnameAndQuery
    const existing = qIndex >= 0 ? pathnameAndQuery.slice(qIndex + 1) : ''
    const params = new URLSearchParams(existing)

    if (resolvedOrderId) {
      params.set('orderId', String(resolvedOrderId))
      params.delete('order_id')
    }
    if (fromApp) {
      params.set('from_app', '1')
    }

    const qs = params.toString()

    return `${pathname}${qs ? `?${qs}` : ''}${hash ? `#${hash}` : ''}`
  } catch (e) {
    return path
  }
}

/** App 进 setup 必须有 orderId，否则拦截 */
export const guardAppSetupOrderId = (search = typeof window !== 'undefined' ? window.location.search : '') => {
  const params = readSearchParams(search)
  const queryOrderId = readOrderIdFromSearch(search)
  const fromAppInUrl = params.get('from_app') === '1'
  const fromApp = isFromAppSession(search)

  if (fromAppInUrl && !queryOrderId) {
    messageInfo({ content: MISSING_ORDER_MSG })

    return { ok: false, blocked: true, orderId: null, fromApp: true }
  }

  const persisted = persistBindOrderIdFromSearch(search)
  const orderId = persisted || queryOrderId || readCachedBindOrderId()

  if (fromApp && !orderId) {
    messageInfo({ content: MISSING_ORDER_MSG })

    return { ok: false, blocked: true, orderId: null, fromApp: true }
  }

  return { ok: true, blocked: false, orderId, fromApp }
}

let unboundOrdersInflight = null

const loadUnboundOrders = async () => {
  if (unboundOrdersInflight) return unboundOrdersInflight

  unboundOrdersInflight = getNotBindOrder()
    .then(res => (Array.isArray(res?.data) ? res.data : []))
    .finally(() => {
      setTimeout(() => {
        unboundOrdersInflight = null
      }, 2000)
    })

  return unboundOrdersInflight
}

/**
 * 从未绑单列表解析 orderId（购买 → 绑定主路径）
 * - 过滤过期单后仅 1 张有效未绑单：自动选中
 * - 多张有效单：优先侧栏已选缓存；setup 初始化时可 autoPickFirst 默认第一张
 */
const resolveFromUnboundOrders = (unbound, { autoPickFirst = false } = {}) => {
  const list = filterValidUnboundOrders(unbound)

  if (list.length === 1) {
    return cacheBindOrder(list[0])
  }

  if (list.length > 1) {
    const selected = readCachedBindOrderId()
    if (selected && list.some(item => getBindOrderId(item) === selected)) {
      return selected
    }
    if (autoPickFirst) {
      return cacheBindOrder(list[0])
    }
  }

  return null
}

/**
 * setup 页初始化：同步 URL/cache；仅一张未绑单时自动挂上 orderId
 */
export const ensureBindOrderContext = async (search = typeof window !== 'undefined' ? window.location.search : '') => {
  const guard = guardAppSetupOrderId(search)
  if (guard.blocked) {
    return { ...guard, needsOrderSelection: false }
  }

  let orderId = guard.orderId
  let needsOrderSelection = false

  if (!orderId) {
    try {
      const unbound = await loadUnboundOrders()
      orderId = resolveFromUnboundOrders(unbound, { autoPickFirst: true })
      if (orderId) {
        syncOrderIdToUrl(orderId)
      } else if (filterValidUnboundOrders(unbound).length > 1) {
        needsOrderSelection = true
      }
    } catch (e) {
      console.error('【ensureBindOrderContext】getNotBindOrder 失败', e)
    }
  } else {
    syncOrderIdToUrl(orderId)
  }

  return {
    ok: true,
    blocked: false,
    orderId,
    fromApp: guard.fromApp,
    needsOrderSelection,
  }
}

/** Connect 点击时解析 orderId，失败则 toast */
export const resolveBindOrderIdWithFeedback = async (t, notify = messageInfo) => {
  try {
    const orderId = await resolveCurrentOrderId()
    if (!orderId) {
      notify({
        content: t?.('Please select a package to bind first') || MISSING_ORDER_MSG,
        type: 'error',
      })

      return null
    }

    return orderId
  } catch (e) {
    notify({
      content: t?.('Network error, please try again') || 'Network error, please try again',
      type: 'error',
    })

    return null
  }
}

/**
 * 绑定 API 用的 order_id（字段名必须是 order_id，不是 orderId）
 * 优先级：显式传入 → URL/缓存 → 未绑单 → 已有设备/支付单号
 */
const resolveCurrentOrderId = async ({ order, orderId } = {}) => {
  if (orderId) return String(orderId)
  if (order?.id) return String(order.id)

  const fromUrl = persistBindOrderIdFromSearch(
    typeof window !== 'undefined' ? window.location.search : ''
  )
  if (fromUrl) return fromUrl

  const cached = readCachedBindOrderId()
  if (cached) return cached

  try {
    const unbound = await loadUnboundOrders()
    const fromUnbound = resolveFromUnboundOrders(unbound, { autoPickFirst: true })
    if (fromUnbound) {
      syncOrderIdToUrl(fromUnbound)

      return fromUnbound
    }
  } catch (e) {
    console.error('【resolveCurrentOrderId】getNotBindOrder 失败', e)
    throw e
  }

  let deviceUser = null
  try {
    deviceUser = JSON.parse(localStorage.getItem('DeviceUser') || 'null')
  } catch (e) {
    // ignore invalid DeviceUser
  }

  let orders = null
  const loadAccountOrders = async () => {
    if (orders) return orders
    const query = { page: 1, pageSize: 9999 }
    try {
      const res = await getUserAccountOrder(query)
      orders = Array.isArray(res?.data) ? res.data : []

      return orders
    } catch (e) {
      console.error('【resolveCurrentOrderId】getUserAccountOrder 失败', e)
      throw e
    }
  }

  // 已有设备重验：优先 DeviceUser.order_id，避免一设备多订单误取
  if (deviceUser?.device_id) {
    const list = await loadAccountOrders()
    const matched = matchOrderForDevice(list, deviceUser)
    if (matched?.id) return String(matched.id)
  }

  // 支付回调写入的 orderNo
  const orderNo = localStorage.getItem('orderNo')
  if (orderNo) {
    const list = await loadAccountOrders()
    const byMerchant = list.find(item => String(item.merchant_no) === String(orderNo))
    if (byMerchant?.id) return String(byMerchant.id)
  }

  return null
}

export default resolveCurrentOrderId
